import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@strivelabs/ui-core/v2';
import { useSidebar } from '@strivelabs/ui-blocks/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useMeteringSummary } from '@/hooks/useMetering';
import { WARN_RATIO } from '@/pages/settings/billing/format';

/** Where the warning sends someone who wants to do something about it. */
const BILLING_USAGE_ROUTE = '/ui/settings/billing/usage';

/**
 * How much of the allowance has gone.
 *
 * Both figures come from the same reading, so their sum is the allowance as the
 * vendor saw it at that moment — which is truer than the plan's nominal
 * allowance, since a top-up raises what is actually available mid-period.
 *
 * @param used - Credits spent in the open period.
 * @param left - Credits still available.
 *
 * @returns The share spent, between 0 and 1, or null when there is no allowance
 * to take a share of.
 */
function usedRatio(used: number, left: number): number | null {
  const allowance = used + left;

  if (!Number.isFinite(allowance) || allowance <= 0) return null;

  return used / allowance;
}

/**
 * Sidebar warning that this workspace is running out of credits.
 *
 * Sits above the identity row rather than on the billing page, because the
 * person who needs to see it is mid-task somewhere else entirely — a warning
 * only visible in Billing is one nobody reads until they have already been cut
 * off.
 *
 * Renders nothing below the threshold. An always-present meter would be ignored
 * by the time it mattered, and it would put a number nobody asked for beside
 * every screen in the product.
 */
export function CreditWarning() {
  const { collapsed } = useSidebar();
  const summary = useMeteringSummary();

  const metering = summary.data;

  // Nothing is claimed while the read is in flight, and nothing is claimed when
  // metering is off — a workspace that is not metered cannot run out.
  if (!metering?.enabled) return null;

  const ratio = usedRatio(metering.credits_used, metering.credits_left);

  // Warned at a fixed share of the allowance. A per-workspace threshold was
  // removed with the usage-alert setting and will return with it.
  if (ratio === null || ratio < WARN_RATIO) return null;

  const percent = Math.min(100, Math.round(ratio * 100));
  const exhausted = metering.credits_left <= 0;
  const message = exhausted
    ? 'Credits used up. Work will not start until you top up.'
    : `${percent}% of this period's credits used.`;

  // The fill tokens are unreadable as text, so the text takes the -text pair.
  // This is the same pairing the v2 badge uses for its warning and danger
  // states, which is what these two are.
  return (
    <Tooltip label={message} side="right" disabled={!collapsed}>
      <Link
        to={BILLING_USAGE_ROUTE}
        aria-label={message}
        className={cn(
          'mx-2 mb-1 flex items-center gap-2 rounded-v2-md border px-2 py-1.5',
          'text-v2-xs transition-colors',
          exhausted
            ? 'border-v2-destructive/30 bg-v2-destructive/10 text-v2-destructive-text hover:bg-v2-destructive/15'
            : 'border-v2-warning/30 bg-v2-warning/10 text-v2-warning-text hover:bg-v2-warning/15',
          collapsed && 'justify-center px-0',
        )}
      >
        <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
        {!collapsed && <span className="truncate">{message}</span>}
      </Link>
    </Tooltip>
  );
}
